import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  Signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { addDays } from "date-fns";

import {
  PlanningEvent,
  PlanningSeverity,
  PlanningStatus,
} from "../../../features/planning/models/planning-event.model";
import {
  PlanningCell,
  PlanningCellMetric,
} from "../../../features/planning/models/planning-cell.model";
import { bucketKey } from "../../../features/planning/utils/cell-keys-for";
import { deriveCellStatus } from "../../../features/planning/utils/derive-cell-status";
import { sortEventsInCell } from "../../../features/planning/utils/sort-events-in-cell";
import { PlanningPrefsService } from "../../../features/planning/services/planning-prefs.service";
import {
  getEventSourceKey,
  PLANNING_SOURCE_META,
  PlanningSourceKey,
} from "../../../features/planning/utils/source-key";
import { VlPlanningBulletCellComponent } from "./cell/vl-planning-bullet-cell.component";
import { VlPlanningListCellComponent } from "./cell/vl-planning-list-cell.component";

/** Timestamp du début du jour local (00:00:00). Utilisé pour comparer des jours. */
function startOfDayTs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

@Component({
  selector: "vl-planning",
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    VlPlanningBulletCellComponent,
    VlPlanningListCellComponent,
  ],
  templateUrl: "./vl-planning.component.html",
  styleUrls: ["./vl-planning.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VlPlanningComponent implements AfterViewInit, OnDestroy {
  private prefsService = inject(PlanningPrefsService);
  private host = inject(ElementRef<HTMLElement>);

  events = input<PlanningEvent[]>([]);
  today = input<Date>(new Date());
  persistenceKey = input<string>("");

  defaultTodayOffset = input<number>(1);
  defaultCellCount = input<number>(5);
  defaultMode = input<"bullet" | "list">("list");
  defaultComfortable = input<boolean>(true);

  /**
   * Liste des sources que le parent alimente effectivement. Utilisée pour :
   * - Filtrer les chips du menu "Sources affichées" (masque les sources
   *   impossibles dans ce contexte — ex: sur la page CF, inutile d'afficher
   *   un toggle "Client" ou "OF")
   * - Masquer entièrement la section si ≤1 source (filtre inutile)
   *
   * Si `undefined`, toutes les sources de PLANNING_SOURCE_META sont listées
   * (comportement par défaut pour rétrocompat).
   */
  availableSources = input<PlanningSourceKey[] | undefined>(undefined);

  cellClick = output<PlanningCell>();
  eventClick = output<PlanningEvent>();
  /** Émis quand l'offset "Prépa PDC J-N" change. Le parent peut recalculer ses events. */
  pdcPrepaOffsetChange = output<number>();

  activeMode = signal<"bullet" | "list">("list");
  activeTodayOffset = signal<number>(1);
  activeComfortable = signal<boolean>(true);
  activePdcPrepaOffset = signal<number>(1);
  activeHiddenSources = signal<PlanningSourceKey[]>([]);

  /** Meta des sources disponibles pour la UI du menu réglages (full list). */
  readonly sourceMeta = PLANNING_SOURCE_META;

  /**
   * Sous-ensemble de `PLANNING_SOURCE_META` restreint à `availableSources`
   * (si fourni par le parent). Utilisé pour afficher uniquement les chips
   * pertinentes dans le contexte courant.
   */
  effectiveSourceMeta = computed(() => {
    const allowed = this.availableSources();
    if (!allowed || allowed.length === 0) return PLANNING_SOURCE_META;
    const set = new Set(allowed);
    return PLANNING_SOURCE_META.filter((m) => set.has(m.key));
  });

  /** Largeur disponible (host) — pilote l'adaptation mobile. */
  hostWidth = signal<number>(0);

  /**
   * Set des clés de jour "populated" (au moins 1 event visible). Inclut la
   * logique de migration retard→today : un event en retard est comptabilisé
   * sur today et non sur sa datePrevu passée. Aligné sur la logique rendering
   * de `cells` pour que effectiveCellCount soit cohérent.
   */
  private populatedKeys = computed<Set<string>>(() => {
    const events = this.events();
    const today = this.today();
    const todayStart = startOfDayTs(today);
    const hidden = this.activeHiddenSources();
    const keys = new Set<string>();

    for (const e of events) {
      if (hidden.length > 0 && hidden.includes(getEventSourceKey(e))) continue;

      // Réplique de la logique migrateIfLate + eventKeys : on ancre l'event
      // sur dateReel si dispo, sinon datePrevu (migré à today si retard).
      let displayDate: Date | null = null;
      if (e.dateReel) {
        displayDate = new Date(e.dateReel);
      } else if (e.datePrevu) {
        const prevu = new Date(e.datePrevu);
        if (
          startOfDayTs(prevu) < todayStart &&
          e.status !== "completed" &&
          e.status !== "cancelled"
        ) {
          displayDate = today; // migré
        } else {
          displayDate = prevu;
        }
      }
      if (displayDate) keys.add(bucketKey(displayDate, "day"));
    }
    return keys;
  });

  /**
   * Nombre de jours affichés — greedy fit.
   *
   * Principe : on ajoute des cellules une par une (en partant de today - offset
   * + shift) tant que la largeur cumulée ne dépasse pas l'espace dispo. Chaque
   * cellule coûte sa largeur estimée :
   * - Populated (au moins 1 event) : ~220px (moyenne du contenu avec titre
   *   + chip + chevron, cap max à 280 côté CSS).
   * - Empty : 60px fixe (cf. `.vl-planning-cell--empty` côté CSS).
   *
   * Résultat : on maximise le nombre de jours affichés sans forcer le layout
   * à déborder. Les cellules chargées gardent leur largeur basée sur le
   * contenu (via flex: 0 0 auto + max-width: 280), les vides restent fixes
   * à 60px.
   */
  effectiveCellCount = computed(() => {
    const w = this.hostWidth();
    if (w === 0) return this.defaultCellCount(); // pré-mesure

    const reserved = 32 * 3 + 20;
    const avail = Math.max(0, w - reserved);
    const gap = 6;
    const POP_WIDTH = 220;
    const EMPTY_WIDTH = 60;

    const today = this.today();
    const offset = this.activeTodayOffset();
    const shift = this.rangeShift();
    const populated = this.populatedKeys();

    let used = 0;
    let count = 0;
    const MAX_CELLS = 60; // safety cap (~2 mois)
    while (count < MAX_CELLS) {
      const dayOffset = count - offset + shift;
      const cellDate = addDays(today, dayOffset);
      const key = bucketKey(cellDate, "day");
      const cellW = populated.has(key) ? POP_WIDTH : EMPTY_WIDTH;
      const addition = (count > 0 ? gap : 0) + cellW;
      if (used + addition > avail) break;
      used += addition;
      count++;
    }
    return Math.max(1, count);
  });

  /** Vrai quand AUCUNE cellule visible n'a d'event : permet aux empties de
   *  s'étirer pour remplir la strip au lieu d'être clusterisées à gauche.
   *  Dès qu'il y a ≥ 1 populated, on revient en mode compact (empties 68px)
   *  et on compte sur un `effectiveCellCount` adaptatif pour afficher plus
   *  de jours si l'espace reste libre. */
  isSparseView = computed(() =>
    this.cells().every((c) => c.events.length === 0),
  );

  /**
   * Offset today recalculé pour rester valide même si effectiveCellCount
   * a réduit la fenêtre (sinon today serait hors vue).
   */
  effectiveTodayOffset = computed(() => {
    const count = this.effectiveCellCount();
    return Math.max(0, Math.min(count - 1, this.activeTodayOffset()));
  });

  rangeShift = signal(0);

  private hostResizeObserver?: ResizeObserver;

  readonly offsetOptions = [0, 1, 2, 3];
  readonly pdcPrepaOptions = [0, 1, 2, 3];

  constructor() {
    effect(
      () => {
        const key = this.persistenceKey();
        const prefs = key ? this.prefsService.load(key) : null;
        this.activeMode.set(prefs?.mode ?? this.defaultMode());
        this.activeTodayOffset.set(
          prefs?.todayOffset ?? this.defaultTodayOffset(),
        );
        this.activeComfortable.set(
          prefs?.comfortable ?? this.defaultComfortable(),
        );
        this.activePdcPrepaOffset.set(prefs?.pdcPrepaOffsetDays ?? 1);
        this.activeHiddenSources.set(prefs?.hiddenSources ?? []);
      },
      { allowSignalWrites: true },
    );
  }

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    this.hostWidth.set(el.getBoundingClientRect().width);
    if (typeof ResizeObserver !== "undefined") {
      this.hostResizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.hostWidth.set(entry.contentRect.width);
        }
      });
      this.hostResizeObserver.observe(el);
    }
  }

  ngOnDestroy(): void {
    this.hostResizeObserver?.disconnect();
  }

  cells: Signal<PlanningCell[]> = computed(() => {
    const events = this.events();
    const today = this.today();
    const count = Math.max(1, this.effectiveCellCount());
    const offset = this.effectiveTodayOffset();
    const shift = this.rangeShift();
    const hidden = this.activeHiddenSources();

    const todayKey = bucketKey(today, "day");
    const todayStart = startOfDayTs(today);

    const buckets = new Map<string, PlanningEvent[]>();
    for (const e of events) {
      // Filtre multi-select : skip les events dont la source est masquée
      if (hidden.length > 0 && hidden.includes(getEventSourceKey(e))) continue;
      const migrated = this.migrateIfLate(e, today, todayStart, todayKey);
      const keys = this.eventKeys(migrated);
      for (const k of keys) {
        const list = buckets.get(k) ?? [];
        list.push(migrated);
        buckets.set(k, list);
      }
    }

    const cells: PlanningCell[] = [];
    for (let i = 0; i < count; i++) {
      const dayOffset = i - offset + shift;
      const cellDate = addDays(today, dayOffset);
      const key = bucketKey(cellDate, "day");
      const cellEvents = sortEventsInCell(buckets.get(key) ?? []);
      const status = deriveCellStatus(cellEvents, cellDate, today);
      const metric = this.buildMetric(cellEvents, key);
      const severity = cellEvents.reduce<PlanningSeverity>(
        (acc, e) =>
          ((e.severity ?? 0) > acc
            ? (e.severity ?? 0)
            : acc) as PlanningSeverity,
        0,
      );
      const anomaliesCount = cellEvents.filter((e) =>
        (["late", "partial", "blocked"] as PlanningStatus[]).includes(e.status),
      ).length;

      cells.push({
        key,
        date: cellDate,
        isToday: key === todayKey,
        isPast: dayOffset < 0,
        isFuture: dayOffset > 0,
        events: cellEvents,
        status,
        metric,
        severity,
        anomaliesCount,
      });
    }
    return cells;
  });

  shiftBack(): void {
    this.rangeShift.update((v) => v - 1);
  }
  shiftForward(): void {
    this.rangeShift.update((v) => v + 1);
  }

  /** Recentre la vue sur today (reset du shift). Visible uniquement quand shift ≠ 0. */
  resetShift(): void {
    this.rangeShift.set(0);
  }

  onCellClick(cell: PlanningCell): void {
    this.cellClick.emit(cell);
  }
  onEventClick(event: PlanningEvent): void {
    this.eventClick.emit(event);
  }

  setMode(mode: "bullet" | "list"): void {
    this.activeMode.set(mode);
    this.savePrefs({ mode });
  }
  setTodayOffset(offset: number): void {
    this.activeTodayOffset.set(offset);
    this.savePrefs({ todayOffset: offset });
  }
  toggleComfortable(): void {
    const next = !this.activeComfortable();
    this.activeComfortable.set(next);
    this.savePrefs({ comfortable: next });
  }
  setPdcPrepaOffset(offset: number): void {
    this.activePdcPrepaOffset.set(offset);
    this.savePrefs({ pdcPrepaOffsetDays: offset });
    this.pdcPrepaOffsetChange.emit(offset);
  }

  isSourceHidden(key: PlanningSourceKey): boolean {
    return this.activeHiddenSources().includes(key);
  }

  toggleSourceHidden(key: PlanningSourceKey): void {
    const current = this.activeHiddenSources();
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    this.activeHiddenSources.set(next);
    this.savePrefs({ hiddenSources: next });
  }

  private savePrefs(partial: Record<string, unknown>): void {
    const key = this.persistenceKey();
    if (key) this.prefsService.save(key, partial);
  }

  /**
   * Un event n'apparaît **qu'une seule fois** dans le planning pour éviter
   * les doublons visuels :
   * - Si `dateReel` est définie (event réalisé) → cellule à cette date
   *   (vue "réalité").
   * - Sinon → cellule à `datePrevu` (vue "planifié, pas encore fait").
   *
   * Le tooltip expose les deux dates (Prévu + Réalisé) donc l'info de
   * planning vs réalité n'est pas perdue, juste plus légère visuellement.
   */
  private eventKeys(event: PlanningEvent): string[] {
    const anchor = event.dateReel ?? event.datePrevu;
    if (!anchor) return [];
    return [bucketKey(new Date(anchor), "day")];
  }

  /**
   * Règle de migration "retard → today" :
   * Si un event a `datePrevu` dans le passé, qu'il n'est pas réalisé
   * (`dateReel` null) et que son status n'est pas terminal (completed/cancelled),
   * on le déplace dans la cellule du jour.
   *
   * Retourne un nouvel event (immutable) avec :
   * - `datePrevu` réécrit à today (preserve le time original du `datePrevu`)
   * - `status = 'late'`, `severity = 2`
   * - `payload.retardJours` enrichi (nb de jours de retard)
   * - `payload.originalDatePrevu` pour garder l'info d'origine (tooltip)
   */
  private migrateIfLate(
    event: PlanningEvent,
    today: Date,
    todayStart: number,
    todayKey: string,
  ): PlanningEvent {
    if (!event.datePrevu || event.dateReel) return event;
    if (event.status === "completed" || event.status === "cancelled") return event;

    const prevu = new Date(event.datePrevu);
    const prevuDay = startOfDayTs(prevu);
    if (prevuDay >= todayStart) return event;

    // Reconstruit datePrevu à today en préservant l'heure originale
    const migrated = new Date(today);
    migrated.setHours(
      prevu.getHours(),
      prevu.getMinutes(),
      prevu.getSeconds(),
      prevu.getMilliseconds(),
    );

    const retardJours = Math.round((todayStart - prevuDay) / (24 * 3600_000));

    return {
      ...event,
      datePrevu: migrated.toISOString(),
      status: "late",
      severity: 2,
      payload: {
        ...(event.payload as any),
        retardJours,
        originalDatePrevu: event.datePrevu,
      },
    };
  }
  private buildMetric(
    events: PlanningEvent[],
    _key: string,
  ): PlanningCellMetric {
    if (events.length === 0) return { prevu: null, reel: null };
    let prevu = 0,
      reel = 0,
      hasQuantite = false;
    for (const e of events) {
      if (e.quantite?.prevu != null) {
        prevu += e.quantite.prevu;
        hasQuantite = true;
      }
      if (e.quantite?.reel != null) {
        reel += e.quantite.reel;
        hasQuantite = true;
      }
    }
    if (hasQuantite) return { prevu: prevu || null, reel: reel || null };
    const prevuCount = events.filter((e) => e.datePrevu != null).length;
    const reelCount = events.filter((e) => e.dateReel != null).length;
    return { prevu: prevuCount || null, reel: reelCount || null };
  }
}
